'use client'

import styled from '@emotion/styled'
import BusinessIcon from '@mui/icons-material/Business'
import ChatIcon from '@mui/icons-material/Chat'
import MemoryIcon from '@mui/icons-material/Memory'
import Box from '@mui/material/Box'
import MuiButton from '@mui/material/Button'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import { useEffect, useState } from 'react'
import Logo from '~/app/_components/brand/logo'
import Drawer from './drawer'

const Header = styled('header')<{ $isScrolled: boolean }>`
  height: var(--header-height);
  position: fixed;
  width: 100%;
  backdrop-filter: blur(16px);
  top: 0;
  z-index: 50;
  transition: all 0.3s ease;
  background-color: rgba(10, 25, 41, 0.8);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  .container {
    height: 100%;
  }

  .grid {
    height: 100%;
  }

  .logo {
    display: flex;
    align-items: center;
    text-decoration: none;

    img,
    svg {
      height: 28px;
      width: auto;

      ${({ theme }) => theme.breakpoints.up('sm')} {
        height: 36px;
      }
    }
  }
`

const NavPill = styled(Box)`
  display: flex;
  align-items: center;
  background: #0d2137;
  border-radius: 9999px;
  padding: 4px 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
`

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  color: white;
  text-decoration: none;
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 9999px;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    color: #67e8f9;
    background: rgba(255, 255, 255, 0.1);
  }
`

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 150)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <Header $isScrolled={isScrolled}>
      <Container maxWidth='lg' className='container'>
        <Grid container justifyContent='space-between' className='grid'>
          <Grid sx={{ display: 'flex', alignItems: 'center' }}>
            <Link href='/' className='logo'>
              <Logo id='home-navbar' gradient={true} />
            </Link>
          </Grid>

          {/* Mobile hamburger */}
          <Grid
            sx={{
              display: { xs: 'flex', lg: 'none' },
              alignItems: 'center',
              px: 2,
              fontSize: '1.4rem',
            }}
          >
            <Drawer isScrolled={isScrolled} />
          </Grid>

          {/* Desktop nav */}
          <Box
            sx={{
              display: { xs: 'none', lg: 'flex' },
              alignItems: 'center',
              gap: 3,
              ml: 4,
            }}
          >
            <NavPill>
              <NavLink href='/about-us'>
                <BusinessIcon
                  sx={{ fontSize: 16, color: '#67e8f9', flexShrink: 0 }}
                />
                About Us
              </NavLink>
              <NavLink href='/technology'>
                <MemoryIcon sx={{ fontSize: 16, color: '#67e8f9', flexShrink: 0 }} />
                Technology
              </NavLink>
              <NavLink href='/consultation'>
                <ChatIcon sx={{ fontSize: 16, color: '#67e8f9', flexShrink: 0 }} />
                Consultation
              </NavLink>
            </NavPill>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
              <MuiButton
                href='/sign/in'
                variant='text'
                size='small'
                sx={{
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Login
              </MuiButton>
              <MuiButton
                href='/sign/up'
                variant='contained'
                size='small'
                sx={{
                  backgroundColor: '#06b6d4',
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': { backgroundColor: '#0891b2' },
                }}
              >
                Sign up
              </MuiButton>
              <MuiButton
                component='a'
                href='https://www.polar-money.com'
                target='_blank'
                rel='noopener noreferrer'
                variant='contained'
                size='small'
                sx={{
                  background: 'linear-gradient(90deg, #22d3ee, #0d9488)',
                  textTransform: 'none',
                  fontWeight: 500,
                  border: 'none',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #06b6d4, #0f766e)',
                  },
                }}
              >
                Polar Money
              </MuiButton>
            </Box>
          </Box>
        </Grid>
      </Container>
    </Header>
  )
}
